"use client";

import { PasswordDescription } from "@/components/PasswordDescription";
import { useErrorHandler } from "@/components/hooks/use-error-handler";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { MAX_PASSWORD_LENGTH } from "@/constants";
import { cn } from "@/lib/utils";
import {
  SignupFormData,
  signup,
  verifyRecaptchaToken,
  type SignupError,
} from "@/server/actions/members/signup/signup";
import { pagesPath } from "@/utils/pages-path";
import { zodResolver } from "@hookform/resolvers/zod";
import EyeIcon from "@public/icons/eye-icon.svg";
import EyeOffIcon from "@public/icons/eye-off-icon.svg";
import Link from "next/link";
import { useState, useTransition, type FC } from "react";
import { useForm } from "react-hook-form";
import { MdHighlightOff } from "react-icons/md";
import { z } from "zod";
import { ReCaptchaProvider, useReCaptcha } from "./recaptcha";

const signupErrorMessages = (error: SignupError) => {
  switch (error) {
    case "validationError":
      return "新規登録に失敗しました。入力内容に誤りがないかご確認ください。";
    default:
      return "新規登録に失敗しました。時間をおいてもう一度お試しいただくか、サポートにお問い合わせください。";
  }
};

export const SignupForm: FC<{
  onCompleteSubmit: (email: string) => void;
  onEmailAlreadyRegistered: () => void;
}> = ({ onCompleteSubmit, onEmailAlreadyRegistered }) => {
  return (
    <ReCaptchaProvider>
      <InnerForm
        onCompleteSubmit={onCompleteSubmit}
        onEmailAlreadyRegistered={onEmailAlreadyRegistered}
      />
    </ReCaptchaProvider>
  );
};

const InnerForm: FC<{
  onCompleteSubmit: (email: string) => void;
  onEmailAlreadyRegistered: () => void;
}> = ({ onCompleteSubmit, onEmailAlreadyRegistered }) => {
  const handleError = useErrorHandler();
  const { toast } = useToast();
  const [passwordMaskEnabled, setPasswordMaskEnabled] = useState(true);
  const [isPending, startTransition] = useTransition();

  const formSchema = z.object({
    email: z.string().min(1, {
      message: "メールアドレスが入力されていません。",
    }),
    password: z.string().min(1, {
      message: "パスワードが入力されていません。",
    }),
  });

  const form = useForm<SignupFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const { execute } = useReCaptcha();

  const onSubmit = form.handleSubmit((data) => {
    startTransition(async () => {
      const executeResult = await execute();

      if (executeResult.type === "failed") {
        form.setError("email", {
          message: "エラーが発生しました。時間をおいてもう一度お試しください。",
        });
        return;
      }

      if (executeResult.type === "success") {
        const recaptchaResult = await verifyRecaptchaToken(executeResult.token);

        if (!recaptchaResult) {
          form.setError("email", {
            message:
              "エラーが発生しました。時間をおいてもう一度お試しください。",
          });
          return;
        }
      }

      const result = await signup(data);

      if (result.success) {
        onCompleteSubmit(data.email);
      } else {
        if (result.error === "emailValidationError") {
          form.setError("email", {
            message: "メールアドレスの形式が正しくありません。",
          });
          return;
        } else if (result.error === "emailAlreadyRegisteredError") {
          onEmailAlreadyRegistered();
          return;
        } else if (result.error === "emailDisallowedDomainError") {
          form.setError("email", {
            message:
              "このメールアドレスはお勤め先のメールアドレスのため、登録することができません。個人でお使いのものを登録してください。",
          });
          return;
        } else if (result.error === "emailIsNotAllowedError") {
          form.setError("email", {
            message:
              "このメールアドレスは登録することができません。個人でお使いのものを登録してください。",
          });
          return;
        } else if (result.error === "weakPasswordError") {
          form.setError("password", {
            message:
              "パスワードは10文字以上で英字の大文字と小文字と数字、記号それぞれ1つ以上を指定してください。" +
              "条件を満たしても推測可能なパスワードは使用できないことがあります。",
          });
          return;
        } else if (result.error === "tooLongPasswordError") {
          form.setError("password", {
            message: `パスワードは${MAX_PASSWORD_LENGTH}文字以下にしてください。`,
          });
          return;
        } else if (result.error === "alreadyLoggedIn") {
          toast({
            title: "エラー",
            description: "ログイン中のため、処理を中断しました。",
            variant: "error",
          });
          handleError("ALREADY_LOGGED_IN");
          return;
        }

        toast({
          title: signupErrorMessages(result.error),
          variant: "error",
        });
      }
    });
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit}>
        <div className="grid grid-cols-1 gap-y-10">
          <FormField
            name="email"
            control={form.control}
            render={({ field, fieldState }) => (
              <FormItem className="relative">
                <FormLabel className="font-bold">メールアドレス</FormLabel>
                <FormControl>
                  <Input
                    placeholder="メールアドレス"
                    autoComplete="email"
                    type="email"
                    disabled={isPending}
                    isInvalid={fieldState.invalid}
                    {...field}
                  />
                </FormControl>
                <button
                  type="button"
                  className={cn(
                    "absolute top-6 right-1 p-2",
                    !field.value && "hidden",
                  )}
                  onClick={() => {
                    form.setValue("email", "");
                  }}
                  aria-label="入力したメールアドレスをクリア"
                >
                  <MdHighlightOff className="fill-monotone-6 stroke-white" />
                </button>
                <FormMessage />
                <FormDescription className="text-xs">
                  <span className="block whitespace-pre-wrap">
                    {
                      "※ 健康保険に関する手続きや情報の確認は退職後も行う場合があります。\n"
                    }
                    {
                      "そのため、お勤め先のメールアドレスではなく、退職後も利用できる個人のメールアドレスをご登録ください。"
                    }
                  </span>
                </FormDescription>
              </FormItem>
            )}
          />

          <FormField
            name="password"
            control={form.control}
            render={({ field, fieldState }) => (
              <FormItem className="relative">
                <FormLabel className="font-bold">パスワード</FormLabel>
                <FormControl>
                  <Input
                    placeholder="パスワード"
                    autoComplete="new-password"
                    type={passwordMaskEnabled ? "password" : "text"}
                    disabled={isPending}
                    isInvalid={fieldState.invalid}
                    {...field}
                  />
                </FormControl>
                <button
                  type="button"
                  className="absolute top-6 right-0 inline-flex h-10 w-12 items-center justify-center p-2"
                  onClick={() => {
                    setPasswordMaskEnabled((p) => !p);
                  }}
                  aria-label={
                    passwordMaskEnabled
                      ? "パスワードを表示する"
                      : "パスワードを隠す"
                  }
                >
                  {passwordMaskEnabled ? (
                    <EyeIcon style={{ color: "hsl(var(--primary-500))" }} />
                  ) : (
                    <EyeOffIcon style={{ color: "hsl(var(--primary-500))" }} />
                  )}
                </button>
                <FormMessage />
                <FormDescription className="text-xs">
                  <PasswordDescription />
                </FormDescription>
              </FormItem>
            )}
          />
          <div>
            <Button
              className="w-full"
              type="submit"
              variant="primary"
              size="lg"
              disabled={isPending}
            >
              新規登録
            </Button>
            <div className="flex items-center justify-center gap-3 pt-8 text-xs sm:gap-6 sm:text-sm">
              <Link
                href={pagesPath.members.login.$url().path}
                className="underline"
              >
                既にアカウントをお持ちの方
              </Link>
              <span className="text-monotone-4">|</span>
              <Link
                href={pagesPath.members.verify_account.$url().path}
                className="underline"
              >
                本人確認のみ未完了の方
              </Link>
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
};
